import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Users, Flame, Plus, Lock } from "lucide-react";
import toast from "react-hot-toast";

export default function Lobby() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Initialize Guest ID on mount
  useEffect(() => {
    const initGuest = async () => {
      try {
        const storedGuestId = localStorage.getItem('guestId');
        const res = await api.post('/sangha/guest', { guestId: storedGuestId });
        if (res.data.guestId) {
          localStorage.setItem('guestId', res.data.guestId);
          // No more custom headers - we pass in payload now
        }
      } catch (err) {
        console.error("Failed to init guest:", err);
      }
    };
    if (!user) {
      initGuest();
    }
  }, [user]);

  // Fetch Rooms
  const fetchRooms = async () => {
    try {
      const res = await api.get('/sangha/rooms');
      setRooms(res.data.rooms || []);
    } catch (err) {
      console.error("Failed to fetch rooms:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [nickname, setNickname] = useState(localStorage.getItem('nickname') || "");
  const [avatar, setAvatar] = useState(localStorage.getItem('avatar') || "🕯️");
  const [circleName, setCircleName] = useState("");
  const [slideProgress, setSlideProgress] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState('forest');

  const avatars = ["🕯️", "🌳", "🌊", "⛰️", "🔥", "✨", "🌙", "🦉", "🦊", "🐘"];

  // Initialize Guest ID on mount
  useEffect(() => {
    const initGuest = async () => {
      try {
        const storedGuestId = localStorage.getItem('guestId');
        const res = await api.post('/sangha/guest', { 
          guestId: storedGuestId,
          nickname: localStorage.getItem('nickname'),
          avatar: localStorage.getItem('avatar')
        });
        if (res.data.guestId) {
          localStorage.setItem('guestId', res.data.guestId);
        }
      } catch (err) {
        console.error("Failed to init guest:", err);
      }
    };
    if (!user) {
      initGuest();
    }
  }, [user]);

  const handleCreateRoom = async () => {
    if (creating) return;
    if (!nickname) {
      setShowProfileModal(true);
      setSlideProgress(0);
      return;
    }

    setCreating(true);
    const guestId = localStorage.getItem('guestId');
    try {
      const res = await api.post('/sangha/rooms', { 
        theme: selectedTheme, 
        name: circleName,
        isPublic: true,
        guestId 
      });
      navigate(`/sangha/${res.data.room.id}`);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error(err.response.data.error || "Room limit reached.");
      } else {
        toast.error("Failed to create room.");
      }
    } finally {
      setCreating(false);
      setSlideProgress(0);
    }
  };

  const handleSaveProfile = async () => {
    if (!nickname.trim()) return toast.error("Nickname required");
    localStorage.setItem('nickname', nickname);
    localStorage.setItem('avatar', avatar);
    
    const guestId = localStorage.getItem('guestId');
    try {
      await api.post('/sangha/guest', { guestId, nickname, avatar });
      setShowProfileModal(false);
      toast.success("Profile ready!");
    } catch (e) {
      toast.error("Failed to update profile");
    }
  };

  const isHinglish = language === 'hi';

  return (
    <div className="min-h-screen bg-[#05070a] flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Profile/Identity Bar */}
      <div className="absolute top-6 right-6 z-20">
        <button 
          onClick={() => setShowProfileModal(true)}
          className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 rounded-full backdrop-blur-xl transition-all"
        >
          <span className="text-lg">{avatar}</span>
          <span className="text-white/80 text-sm font-medium">{nickname || (isHinglish ? "Pehchan Banayein" : "Set Identity")}</span>
          <div className="w-6 h-6 flex items-center justify-center bg-white/5 rounded-full">
            <Plus className="w-3.5 h-3.5 text-white/40" />
          </div>
        </button>
      </div>

      {/* Immersive Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-900/20 blur-[150px] rounded-full"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-emerald-900/10 blur-[150px] rounded-full"></div>
      </div>

      <div className="max-w-5xl w-full relative z-10">
        <div className="text-center mb-16 animate-in fade-in slide-in-from-top-4 duration-1000">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            {isHinglish ? "Sanghas" : "The Circles"}
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed font-light">
            {isHinglish 
              ? "Ek aisi jagah jahan aap apna man halka kar sakte hain, bina kisi pehchan ke." 
              : "Step into an ephemeral sanctuary. Share, reflect, and dissolve into the collective."}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-stretch">
          {/* Create Room Section */}
          <div className="lg:w-[40%]">
            <div className="h-full bg-white/[0.03] backdrop-blur-2xl p-8 rounded-[2.5rem] border border-white/10 shadow-2xl flex flex-col">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-500/10 rounded-2xl mb-6">
                <Flame className="w-8 h-8 text-orange-500 animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                {isHinglish ? "Naya Sangha Banayein" : "Light a New Fire"}
              </h2>
              <p className="text-gray-400 mb-8 text-sm leading-relaxed">
                {isHinglish 
                  ? "8 ghante ke liye ek naya circle banayein. Max 6 members."
                  : "Create an ephemeral room that lasts for 8 hours. Max 6 members."}
              </p>

              <div className="mb-6">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] mb-2 block ml-1">
                    {isHinglish ? "Circle ka Naam (Optional)" : "Circle Name (Optional)"}
                </label>
                <input 
                    type="text"
                    value={circleName}
                    onChange={(e) => setCircleName(e.target.value)}
                    placeholder={isHinglish ? "Pawan Shanti, Meditation..." : "Morning Zen, Heart Talk..."}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 text-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 transition-all placeholder:text-white/10"
                />
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  { id: 'forest', label: isHinglish ? "Jungle Theme" : "Forest Theme", icon: "🌳", bg: "emerald" },
                  { id: 'beach', label: isHinglish ? "Samundar Theme" : "Beach Theme", icon: "blue" },
                  { id: 'mountains', label: isHinglish ? "Pahadi Theme" : "Mountain Theme", icon: "slate" }
                ].map(theme => (
                  <button 
                    key={theme.id}
                    onClick={() => setSelectedTheme(theme.id)}
                    className={`w-full py-4 px-6 bg-white/[0.02] border transition-all flex items-center justify-between group rounded-2xl ${
                        selectedTheme === theme.id 
                        ? `border-${theme.bg}-500/50 bg-${theme.bg}-500/10 ring-1 ring-${theme.bg}-500/20` 
                        : "border-white/5 hover:border-white/20"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                        <span className={`text-xl ${selectedTheme === theme.id ? "" : "grayscale"}`}>{theme.id === 'forest' ? '🌳' : theme.id === 'beach' ? '🌊' : '⛰️'}</span>
                        <span className={selectedTheme === theme.id ? "text-white font-bold" : "text-gray-400"}>{theme.label}</span>
                    </span>
                    {selectedTheme === theme.id && <div className={`w-2 h-2 rounded-full bg-${theme.bg}-400 shadow-[0_0_10px_rgba(52,211,153,0.5)]`}></div>}
                  </button>
                ))}
              </div>

              {/* Slide Captcha */}
              <div className="relative h-16 bg-white/5 rounded-2xl border border-white/10 overflow-hidden mb-6">
                <div 
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-600/20 to-orange-500/40 transition-all pointer-events-none"
                    style={{ width: `${slideProgress}%` }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-xs font-bold tracking-widest text-white/30 uppercase">
                    Slide to Light the Fire
                </div>
                <input 
                    type="range"
                    min="0"
                    max="100"
                    value={slideProgress}
                    onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setSlideProgress(val);
                        if (val >= 98) handleCreateRoom();
                    }}
                    onMouseUp={() => slideProgress < 98 && setSlideProgress(0)}
                    onTouchEnd={() => slideProgress < 98 && setSlideProgress(0)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div 
                    className="absolute top-1 bottom-1 w-14 bg-white/10 border border-white/20 rounded-xl flex items-center justify-center transition-all shadow-xl pointer-events-none"
                    style={{ left: `calc(${slideProgress}% - ${slideProgress * 0.56}px + 4px)` }}
                >
                    <Flame className="w-6 h-6 text-orange-500" />
                </div>
              </div>

              {!user && (
                <div className="p-5 bg-indigo-500/5 rounded-2xl border border-indigo-500/10 flex items-start gap-3">
                  <Lock className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-indigo-300/70 leading-relaxed">
                    {isHinglish 
                      ? "Anonymous users ke liye 8 ghante. Signup par 16 ghante."
                      : "Anonymous fires burn for 8 hours. Authenticate to double the lifespan."}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Active Rooms List */}
          <div className="lg:w-[60%] flex flex-col">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-3">
                    <Users className="w-6 h-6 text-indigo-400" />
                    {isHinglish ? "Active Sanghas" : "Active Circles"}
                </h3>
                <span className="text-xs font-medium text-gray-500 tracking-widest uppercase bg-white/5 px-3 py-1 rounded-full border border-white/5">
                    {rooms.length} Active
                </span>
            </div>

            {loading ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-32 bg-white/[0.02] border border-white/5 rounded-[2rem] animate-pulse"></div>
                ))}
              </div>
            ) : rooms.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center bg-white/[0.02] border border-dashed border-white/10 rounded-[2.5rem] p-12">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-gray-600" />
                </div>
                <p className="text-gray-500 text-center max-w-xs">
                  {isHinglish 
                    ? "Abhi koi public room nahi hai. Apna naya Sangha shuru karein."
                    : "The landscape is quiet. Be the one to light the first fire."}
                </p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 overflow-y-auto max-h-[600px] pr-2 custom-scrollbar">
                {rooms.map(room => (
                  <div 
                    key={room.id}
                    onClick={() => {
                        if (!nickname) {
                            setShowProfileModal(true);
                        } else {
                            navigate(`/sangha/${room.id}`);
                        }
                    }}
                    className="group bg-white/[0.03] p-6 rounded-[2rem] border border-white/5 hover:border-white/20 hover:bg-white/[0.05] transition-all cursor-pointer flex flex-col relative overflow-hidden shadow-xl"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 blur-3xl rounded-full -mr-12 -mt-12 group-hover:bg-white/10 transition-colors"></div>
                    
                    <div className="flex items-center justify-between mb-4 relative z-10">
                      <span className="text-3xl filter drop-shadow-lg">
                        {room.theme === 'forest' ? '🌳' : room.theme === 'beach' ? '🌊' : '⛰️'}
                      </span>
                      <div className="flex items-center gap-2">
                        {((user && room.hostUserId === user.id) || (localStorage.getItem('guestId') === room.hostGuestId)) && (
                            <span className="text-[9px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-lg">
                                Your Fire
                            </span>
                        )}
                        <span className="text-[10px] font-bold tracking-widest uppercase px-3 py-1 bg-white/5 text-gray-400 border border-white/10 rounded-full group-hover:bg-emerald-500/10 group-hover:text-emerald-400 transition-colors">
                            {Math.max(1, Math.floor((new Date(room.expiresAt) - new Date()) / (1000 * 60 * 60)))}h left
                        </span>
                      </div>
                    </div>
                    
                    <h4 className="font-bold text-white mb-1 capitalize text-lg relative z-10 truncate pr-4">
                      {room.name || `${room.theme} ${isHinglish ? "Sangha" : "Circle"}`}
                    </h4>
                    
                    <div className="flex items-center gap-4 mt-auto relative z-10">
                        <p className="text-xs text-gray-500 flex items-center gap-1.5 font-medium">
                            <Users className="w-3.5 h-3.5 text-indigo-400" /> {room.travelersCount || 0}
                        </p>
                        <div className="h-1 w-1 rounded-full bg-gray-700"></div>
                        <p className="text-[10px] text-gray-600 font-bold tracking-widest uppercase">
                            Join Now
                        </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Setup Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md">
            <div className="bg-[#0a0c10] border border-white/10 w-full max-w-md p-8 rounded-[2.5rem] shadow-2xl animate-in fade-in zoom-in duration-300">
                <h3 className="text-2xl font-bold text-white mb-2">Choose your Identity</h3>
                <p className="text-gray-500 text-sm mb-8">Before you join the circle, who shall you be today?</p>
                
                <div className="space-y-6">
                    <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Nickname</label>
                        <input 
                            type="text"
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value)}
                            placeholder="Type a nickname..."
                            className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
                        />
                    </div>
                    
                    <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3 block">Avatar</label>
                        <div className="grid grid-cols-5 gap-3">
                            {avatars.map(a => (
                                <button 
                                    key={a}
                                    onClick={() => setAvatar(a)}
                                    className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all ${
                                        avatar === a ? "bg-indigo-600 shadow-lg scale-110" : "bg-white/5 hover:bg-white/10"
                                    }`}
                                >
                                    <span className="text-xl">{a}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <button 
                        onClick={handleSaveProfile}
                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl shadow-xl shadow-indigo-900/20 transition-all active:scale-95"
                    >
                        Ready to Join
                    </button>
                    
                    <button 
                        onClick={() => setShowProfileModal(false)}
                        className="w-full py-2 text-gray-600 hover:text-gray-400 text-sm font-medium transition-all"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}
